import React, { useEffect, useRef, useState, useCallback } from "react";
import "./AnalyticsDetail.css";
import Topbar from "../Global/topbar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import { getAnalyticsDetail } from "../../services/api/analytics";
import { CircularProgress, Chip } from "@mui/material";
import { handleToast } from "../../utils";
import PaginationUI from "../Table container/PaginationUI";

const TYPE_CONFIG = {
  new_users: {
    title: "New Users",
    columns: ["Name", "Email", "Phone", "Created At"],
    fields: ["name", "email", "phone_number", "created_at"],
  },
  new_consultants: {
    title: "New Consultants",
    columns: ["Name", "Email", "Phone", "Status", "Created At"],
    fields: ["name", "email", "phone_number", "approve_status", "created_at"],
  },
  active_users: {
    title: "Active Users",
    columns: ["Name", "Email", "Phone", "Status"],
    fields: ["name", "email", "phone_number", "active_status"],
  },
  active_consultants: {
    title: "Active Consultants",
    columns: ["Name", "Email", "Phone", "Status", "Rating"],
    fields: ["name", "email", "phone_number", "active_status", "rating"],
  },
  total_calls: {
    title: "Total Calls",
    columns: ["User", "Consultant", "Duration (min)", "Status", "Created At"],
    fields: ["user_name", "consultant_name", "call_duration_minutes", "call_status", "created_at"],
  },
  calls_answered: {
    title: "Answered Calls",
    columns: ["User", "Consultant", "Duration (min)", "Status", "Created At"],
    fields: ["user_name", "consultant_name", "call_duration_minutes", "call_status", "created_at"],
  },
  calls_missed: {
    title: "Missed Calls",
    columns: ["User", "Consultant", "Status", "Created At"],
    fields: ["user_name", "consultant_name", "call_status", "created_at"],
  },
  calls_rejected: {
    title: "Rejected Calls",
    columns: ["User", "Consultant", "Status", "Created At"],
    fields: ["user_name", "consultant_name", "call_status", "created_at"],
  },
  total_recharges: {
    title: "Total Recharges",
    columns: ["User", "Amount", "Type", "Status", "Created At"],
    fields: ["user_name", "amount", "daily_transaction_type", "transaction_status", "created_at"],
  },
  recharge_revenue: {
    title: "Recharge Revenue",
    columns: ["User", "Amount", "Type", "Status", "Created At"],
    fields: ["user_name", "amount", "daily_transaction_type", "transaction_status", "created_at"],
  },
  call_revenue: {
    title: "Call Revenue",
    columns: ["User", "Amount", "Type", "Status", "Created At"],
    fields: ["user_name", "amount", "daily_transaction_type", "transaction_status", "created_at"],
  },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

const getStatusChip = (status) => {
  if (!status) return "-";
  const lower = String(status).toLowerCase();
  let color = "default";
  if (["approved", "online", "answered", "ended"].includes(lower)) color = "success";
  else if (["pending", "busy", "ringing"].includes(lower)) color = "warning";
  else if (["rejected", "offline", "missed"].includes(lower)) color = "error";
  return <Chip label={status} color={color} size="small" variant="outlined" />;
};

const renderCell = (item, field) => {
  // For call logs: find user/consultant from participants by entity field
  if (field === "user_name") {
    const participants = item?.participants;
    if (Array.isArray(participants)) {
      const userP = participants.find((p) => p?.entity === "user");
      if (userP) return userP.name || userP.google_name || item?.user_id || "-";
    }
    const user = item?.user;
    if (user) return user?.name || user?.google_name || item?.user_id || "-";
    return item?.user_name || item?.user_id || "-";
  }
  if (field === "consultant_name") {
    const participants = item?.participants;
    if (Array.isArray(participants)) {
      const consultantP = participants.find((p) => p?.entity === "consultant");
      if (consultantP) return consultantP.name || consultantP.google_name || item?.consultant_id || "-";
    }
    return item?.consultant_name || item?.consultant_id || "-";
  }
  const value = item?.[field];
  if (["approve_status", "active_status", "call_status", "transaction_status"].includes(field)) {
    return getStatusChip(value);
  }
  if (field === "created_at") {
    return formatDate(value);
  }
  if (field === "call_duration_minutes") {
    const start = item?.call_start_time;
    const end = item?.call_end_time;
    if (start && end) {
      const diff = (new Date(end) - new Date(start)) / 60000;
      return diff > 0 ? diff.toFixed(1) : "0";
    }
    return "-";
  }
  if (field === "amount") {
    return value !== undefined && value !== null ? `₹${Number(value).toLocaleString("en-IN")}` : "-";
  }
  if (field === "daily_transaction_type") {
    if (!value) return "-";
    return String(value).replace(/#/g, " ").replace(/_/g, " ");
  }
  if (value === undefined || value === null || value === "") return "-";
  return String(value);
};

export default function AnalyticsDetail() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { type, date, title: customTitle } = state || {};

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const startKeys = useRef({ 0: null });

  const config = TYPE_CONFIG[type];

  const fetchData = useCallback(
    async (pageNum) => {
      setLoading(true);
      try {
        const sk = startKeys.current[pageNum] || null;
        const response = await getAnalyticsDetail(type, date, rowsPerPage, sk);
        const result = response?.data?.data;
        const items = result?.data || [];
        const nextKey = result?.start_key || null;

        setData(items);
        if (nextKey) {
          startKeys.current[pageNum + 1] = nextKey;
        }
      } catch (error) {
        handleToast(error?.response?.data);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [type, date, rowsPerPage]
  );

  useEffect(() => {
    if (!type || !config) {
      navigate("/admin/analytics", { replace: true });
      return;
    }
    fetchData(0);
  }, [type, config, fetchData, navigate]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(newPage);
  };

  const handleRowsPerPageChange = (newLimit) => {
    setRowsPerPage(newLimit);
    setPage(0);
    startKeys.current = { 0: null };
  };

  if (!type || !config) return null;

  const displayTitle = customTitle || config.title;

  return (
    <div className="analytics-detail-main">
      <Topbar />
      <div className="analytics-detail-container">
        <div className="analytics-detail-header">
          <button className="analytics-detail-back" onClick={() => navigate("/admin/analytics")}>
            <ArrowBackIcon style={{ fontSize: "1.5rem" }} />
          </button>
          <div>
            <h1>{displayTitle}</h1>
            {date && <span className="analytics-detail-date">{date}</span>}
          </div>
        </div>

        {loading ? (
          <div className="analytics-detail-loading">
            <CircularProgress />
          </div>
        ) : data.length === 0 ? (
          <div className="analytics-detail-empty">
            <h3>No data found</h3>
          </div>
        ) : (
          <>
            <div className="analytics-detail-table-wrapper">
              <table className="analytics-detail-table">
                <thead>
                  <tr>
                    <th>#</th>
                    {config.columns.map((col, i) => (
                      <th key={i}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={item?.id || item?.SK || index}>
                      <td>{page * rowsPerPage + index + 1}</td>
                      {config.fields.map((field, i) => (
                        <td key={i}>{renderCell(item, field)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="analytics-detail-pagination">
              <div className="pagination-controls">
                <label>Rows per page: </label>
                <select
                  value={rowsPerPage}
                  onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                >
                  {[10, 20, 40, 50, 100].map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pagination-buttons">
                <button disabled={page === 0} onClick={() => handlePageChange(page - 1)}>
                  Previous
                </button>
                <span>Page {page + 1}</span>
                <button
                  disabled={data.length < rowsPerPage || !startKeys.current[page + 1]}
                  onClick={() => handlePageChange(page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
